import { deepClone } from "../../../utils";
import { PrivateLoanType } from "../../../forecast";
const MONTHS_IN_YEAR = 12;
// In 2025 the interest deduction for private loans will be reduced to 50% of the interest cost, in 2026 deduction will be removed.
export const PRIVATE_LOAN_INTEREST_DEDUCTION_REDUCTION_RATE = 0.5;
/**
 * @param privateLoanType Private loan type
 * @returns True if interest is deductible
 */
export function isInterestDeductiblePrivateLoan(privateLoanType) {
    return privateLoanType === PrivateLoanType.INSTALMENT_CREDIT || privateLoanType === PrivateLoanType.UNSECURED_LOAN;
}
/**
 * @param deductionThreshold Deduction threshold
 * @param baseInterestDeductionRate Base interest deduction rate
 * @param extraInterestDeductionRate Extra interest deduction rate
 * @param deductibleInterestExpenseBeforeDeduction Deductible yearly interest expense
 * @returns Yearly interest deduction
 */
function calculateInterestDeduction(deductionThreshold, baseInterestDeductionRate, extraInterestDeductionRate, deductibleInterestExpenseBeforeDeduction) {
    const bottomDeduction = Math.min(deductibleInterestExpenseBeforeDeduction, deductionThreshold) * baseInterestDeductionRate;
    const topDeduction = Math.max(deductibleInterestExpenseBeforeDeduction - deductionThreshold, 0) * extraInterestDeductionRate;
    return bottomDeduction + topDeduction;
}
/**
 * Calculates yearly interest deduction for main mortgage, additional mortgage and private loans
 *
 * @param interestExpense Yearly interest expense before deduction
 * @param adults Number of adults in household
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Yearly interest deduction
 */
export function calculateMultipleLoansInterestDeduction(interestExpense, adults, localizationParameters) {
    const { interestDeductionThreshold, baseInterestDeductionRate, extraInterestDeductionRate } = localizationParameters;
    const adultsResponsibleForMortgages = Math.min(adults, 2);
    const currentInterestDeductionThreshold = interestDeductionThreshold * adultsResponsibleForMortgages;
    // Private loan deduction
    const deductionThresholdPrivateLoan = currentInterestDeductionThreshold;
    const deductiblePrivateLoanInterestExpense = interestExpense.yearPrivateLoanInterest * PRIVATE_LOAN_INTEREST_DEDUCTION_REDUCTION_RATE;
    const privateLoanInterestDeduction = calculateInterestDeduction(deductionThresholdPrivateLoan, baseInterestDeductionRate, extraInterestDeductionRate, deductiblePrivateLoanInterestExpense);
    // Additional mortgage deduction
    const deductionThresholdAdditionalMortgage = Math.max(deductionThresholdPrivateLoan - deductiblePrivateLoanInterestExpense, 0);
    const additionalMortgageInterestDeduction = calculateInterestDeduction(deductionThresholdAdditionalMortgage, baseInterestDeductionRate, extraInterestDeductionRate, interestExpense.yearAdditionalMortgageInterest);
    // Main Mortgage deduction
    const deductionThresholdMainMortgage = Math.max(deductionThresholdAdditionalMortgage - interestExpense.yearAdditionalMortgageInterest, 0);
    const mainMortgageInterestDeduction = calculateInterestDeduction(deductionThresholdMainMortgage, baseInterestDeductionRate, extraInterestDeductionRate, interestExpense.yearMainMortgageInterest);
    return { mainMortgageInterestDeduction, additionalMortgageInterestDeduction, privateLoanInterestDeduction };
}
/**
 * Calculates yearly interest deduction rate
 *
 * @param interestExpense Yearly interest expense before deduction
 * @param adults Number of adults in household
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Yearly interest deduction rate
 */
export function calculateInterestDeductionRate(interestExpense, adults, localizationParameters) {
    const { baseInterestDeductionRate } = localizationParameters;
    const { yearMainMortgageInterest, yearAdditionalMortgageInterest, yearPrivateLoanInterest } = interestExpense;
    const yearInterest = yearMainMortgageInterest + yearAdditionalMortgageInterest + yearPrivateLoanInterest;
    if (yearInterest <= 0) {
        return {
            mainMortgageInterestDeductionRate: baseInterestDeductionRate,
            additionalMortgageInterestDeductionRate: baseInterestDeductionRate,
            privateLoanInterestDeductionRate: baseInterestDeductionRate,
        };
    }
    const { mainMortgageInterestDeduction, additionalMortgageInterestDeduction, privateLoanInterestDeduction } = calculateMultipleLoansInterestDeduction(interestExpense, adults, localizationParameters);
    const mainMortgageInterestDeductionRate = yearMainMortgageInterest ? mainMortgageInterestDeduction / yearMainMortgageInterest : 0;
    const additionalMortgageInterestDeductionRate = yearAdditionalMortgageInterest
        ? additionalMortgageInterestDeduction / yearAdditionalMortgageInterest
        : 0;
    const privateLoanInterestDeductionRate = yearPrivateLoanInterest ? privateLoanInterestDeduction / yearPrivateLoanInterest : 0;
    return { mainMortgageInterestDeductionRate, additionalMortgageInterestDeductionRate, privateLoanInterestDeductionRate };
}
/**
 * Correction of interest, household, housing and additional mortgage expenses,
 * and discretionary income due to interest deduction at the end of the year.
 *
 * The input forecastResult will be modified by the function.
 *
 * @param forecastResult Forecast result so far. Will be modified by function.
 * @param mortgageLocalizationParameters Mortgage localization parameters
 * @param month Current month in forecast. Zero indexed.
 * @returns Void
 */
export function calculateForecastResultAfterInterestDeduction(forecastResult, adults, mortgageLocalizationParameters) {
    const forecastResultClone = deepClone(forecastResult);
    const nrOfSimulationYears = forecastResult.length / MONTHS_IN_YEAR;
    for (let yearIndex = 0; yearIndex < nrOfSimulationYears; yearIndex++) {
        const yearResult = forecastResult.slice(12 * yearIndex, 12 * yearIndex + 12); // Get one year of forecast results
        const yearInterest = yearResult.reduce((acc, monthResult) => {
            acc.yearMainMortgageInterest += monthResult.totalMonthlyInterestBeforeDeduction;
            acc.yearAdditionalMortgageInterest += monthResult.additionalMortgageInterest;
            acc.yearPrivateLoanInterest += monthResult.privateLoans.reduce((monthInterest, monthPrivateLoan) => {
                let loanInterest = 0;
                if (isInterestDeductiblePrivateLoan(monthPrivateLoan.type)) {
                    loanInterest = monthPrivateLoan.interest;
                }
                return monthInterest + loanInterest;
            }, 0);
            return acc;
        }, { yearMainMortgageInterest: 0, yearAdditionalMortgageInterest: 0, yearPrivateLoanInterest: 0 });
        const interestDeductionRates = calculateInterestDeductionRate(yearInterest, adults, mortgageLocalizationParameters);
        for (let monthIndex = 0; monthIndex < yearResult.length; monthIndex++) {
            // Main mortgage interest deduction
            const oldMainMortgageMonthlyInterest = yearResult[monthIndex].totalMonthlyInterestBeforeDeduction;
            const newMainMortgageMonthlyInterest = Math.round(oldMainMortgageMonthlyInterest * (1 - interestDeductionRates.mainMortgageInterestDeductionRate));
            const mainMortgageInterestDeduction = oldMainMortgageMonthlyInterest - newMainMortgageMonthlyInterest;
            // Additional mortgage interest deduction
            const oldAdditionalMortgageInterest = yearResult[monthIndex].additionalMortgageInterest;
            const newAdditionalMortgageInterest = Math.round(oldAdditionalMortgageInterest * (1 - interestDeductionRates.additionalMortgageInterestDeductionRate));
            const additionalMortgageInterestDeduction = oldAdditionalMortgageInterest - newAdditionalMortgageInterest;
            // Private loan interest deduction
            let privateLoansMonthInterestDeduction = 0;
            yearResult[monthIndex].privateLoans.forEach((privateLoan, loanIndex) => {
                if (isInterestDeductiblePrivateLoan(privateLoan.type)) {
                    const oldInterest = privateLoan.interest;
                    const newInterest = Math.round(oldInterest * (1 - interestDeductionRates.privateLoanInterestDeductionRate));
                    privateLoansMonthInterestDeduction += oldInterest - newInterest;
                    /* eslint-disable no-param-reassign */
                    forecastResultClone[12 * yearIndex + monthIndex].privateLoans[loanIndex].interest = newInterest;
                }
            });
            const totalMonthlyDeduction = mainMortgageInterestDeduction + additionalMortgageInterestDeduction + privateLoansMonthInterestDeduction;
            /* eslint-disable no-param-reassign */
            forecastResultClone[12 * yearIndex + monthIndex].householdExpenses -= totalMonthlyDeduction;
            forecastResultClone[12 * yearIndex + monthIndex].discreationaryIncome += totalMonthlyDeduction;
            forecastResultClone[12 * yearIndex + monthIndex].totalMonthlyInterestAfterDeduction = newMainMortgageMonthlyInterest;
            forecastResultClone[12 * yearIndex + monthIndex].housingExpensesAfterTaxDeduction =
                yearResult[monthIndex].housingExpensesBeforeTaxDeduction - mainMortgageInterestDeduction;
            forecastResultClone[12 * yearIndex + monthIndex].additionalMortgageInterest = newAdditionalMortgageInterest;
        }
    }
    return forecastResultClone;
}
